"use client";

import { LockIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING_INSET } from "./focus-ring";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

// Chart lenses (CHART-LENSES 2026-10-21; KPI Beast's mode bar and scored seat cards, in the DTP
// look): the tabs across the top of the org chart, the prominent score a card shows under a
// lens, the band dot, and the inline legend of a lens's steps. Each lens has a hue
// (`--lens-<name>` in tokens.css, `data-lens` in lens.css sets `--lens`); a band has a colour
// (the scorecard's mode-invariant band tokens: green, amber, red, grey for none) and is never
// colour alone — the number or the note sits beside it.

export type LensBandTone = "green" | "amber" | "red" | "none";

export const LENS_BAND_SWATCH: Record<LensBandTone, string> = {
  green: "bg-band-great",
  amber: "bg-band-average",
  red: "bg-band-low",
  none: "bg-band-insufficient-data",
};

const BAND_TEXT: Record<LensBandTone, string> = {
  green: "text-success-text",
  amber: "text-warning-text",
  red: "text-danger-text",
  none: "text-faint",
};

export const LENS_BAND_LABELS: Record<LensBandTone, string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
  none: "No data",
};

export type LensTab = {
  value: string;
  label: React.ReactNode;
  /** The viewer may not open it; `note` says to whom it is shown. */
  locked?: boolean;
  note?: string;
};

export type LensTabsProps = Omit<React.ComponentProps<"div">, "onChange"> & {
  tabs: LensTab[];
  value: string;
  onValueChange: (value: string) => void;
  "aria-label": string;
};

/**
 * The mode bar: a radiogroup of 32 px segments, each with its lens's hue as a square; the
 * chosen one wears a tint of the hue and a rule along the bottom (lens.css). A locked lens
 * stays in the bar with a lock and a tooltip, so a member sees what the chart carries beyond
 * their floor, and cannot open it.
 */
export function LensTabs({
  tabs,
  value,
  onValueChange,
  className,
  "aria-label": ariaLabel,
  ...props
}: LensTabsProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-slot="lens-tabs"
      className={cn(
        "flex h-9 min-w-0 shrink-0 items-stretch gap-0.5 strip-scrollrounded-md border border-border bg-card p-0.5",
        className,
      )}
      {...props}
    >
      {tabs.map((t) => {
        const button = (
          // biome-ignore lint/a11y/useSemanticElements: a segmented bar of radio-like buttons (an <input type="radio"> cannot carry the swatch, the lock and the tooltip trigger); the group above is the radiogroup.
          <button
            key={t.value}
            type="button"
            role="radio"
            aria-checked={t.value === value}
            aria-disabled={t.locked ? true : undefined}
            data-slot="lens-tab"
            data-lens={t.value}
            data-locked={t.locked ? "" : undefined}
            onClick={() => {
              if (!t.locked) onValueChange(t.value);
            }}
            className={cn(
              "inline-flex h-full shrink-0 items-center gap-1.5 rounded-sm px-2.5 text-xs font-medium whitespace-nowrap text-muted-foreground transition-colors duration-(--dur-fast) hover:text-foreground",
              FOCUS_RING_INSET,
              t.locked && "cursor-not-allowed text-faint hover:text-faint",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "size-2 shrink-0 rounded-[2px] bg-(--lens)",
                t.value !== value && "opacity-60",
              )}
            />
            {t.label}
            {t.locked ? <LockIcon aria-hidden="true" className="size-3 text-faint" /> : null}
          </button>
        );
        if (!t.locked || !t.note) return button;
        return (
          <Tooltip key={t.value}>
            <TooltipTrigger render={button} />
            <TooltipContent>{t.note}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** The band's dot: 8 px, in the band's colour; `label` names it for screen readers. */
export function LensBandDot({
  band,
  className,
  ...props
}: { band: LensBandTone } & Omit<React.ComponentProps<"span">, "children">) {
  return (
    <span
      data-slot="lens-band-dot"
      data-band={band}
      role="img"
      aria-label={LENS_BAND_LABELS[band]}
      className={cn("inline-block size-2 shrink-0 rounded-full", LENS_BAND_SWATCH[band], className)}
      {...props}
    />
  );
}

export type LensScoreProps = Omit<React.ComponentProps<"span">, "children"> & {
  score: number | null;
  band: LensBandTone;
  /** After the number: "%" for a share, "" for a 0–100 score. */
  unit?: string;
  /** What to say instead of a number ("No open tasks"). */
  note?: string;
  /** `lg` is the card's headline (18 px); `md` a row's (14 px). */
  size?: "md" | "lg";
};

/**
 * A lens's number for one seat: the dot, then the score in the band's text colour, tabular, or
 * the note in muted text when there is nothing to score. Never colour alone: the number and the
 * dot's label carry the band.
 */
export function LensScore({
  score,
  band,
  unit = "",
  note,
  size = "lg",
  className,
  ...props
}: LensScoreProps) {
  const none = score === null || band === "none";
  return (
    <span
      data-slot="lens-score"
      data-band={band}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 tabular-nums",
        size === "lg" ? "text-lg leading-6 font-semibold" : "text-sm leading-5 font-medium",
        none ? "text-faint" : BAND_TEXT[band],
        className,
      )}
      {...props}
    >
      <LensBandDot band={none ? "none" : band} className={size === "lg" ? "size-2.5" : ""} />
      {none ? (
        // Unscored is a grey "—", never a 0; the headline keeps its note for the tooltip and
        // screen readers, a row (md) spells it out after the dash.
        <span
          data-slot="lens-unscored"
          title={note ?? "No data"}
          className="inline-flex min-w-0 items-baseline gap-1.5"
        >
          <span aria-hidden="true">—</span>
          <span className={cn("font-normal", size === "lg" ? "sr-only" : "truncate text-xs")}>
            {note ?? "No data"}
          </span>
        </span>
      ) : (
        <span>
          {score}
          {unit ? (
            <span className="text-[max(11px,0.75em)] font-medium opacity-80">{unit}</span>
          ) : null}
        </span>
      )}
    </span>
  );
}

export type DimensionBar = {
  /** The lens (`data-lens` sets the hue). */
  lens: string;
  label: string;
  /** `active`: the chart's lens; `has`: the seat carries it; `empty`: nothing in it on this seat. */
  state: "active" | "has" | "empty";
};

/**
 * CHART-KB: the thin bars along a card's top — one segment per lens, in the lens's hue (KPI
 * Beast's seat cards carried one bar per space the role had). The chart's lens is full and a
 * little taller, a lens the seat has nothing in is faint; a lens the viewer may not see on the
 * seat is simply not a segment. The row names every segment for screen readers.
 */
export function DimensionBars({
  bars,
  className,
  ...props
}: { bars: readonly DimensionBar[] } & Omit<React.ComponentProps<"div">, "children">) {
  return (
    <div
      data-slot="dimension-bars"
      role="img"
      aria-label={bars
        .map((b) => `${b.label}: ${b.state === "empty" ? "nothing" : "yes"}`)
        .join(", ")}
      className={className}
      {...props}
    >
      {bars.map((b) => (
        <span
          key={b.lens}
          data-slot="dimension-bar"
          data-lens={b.lens}
          data-state={b.state}
          title={b.label}
        />
      ))}
    </div>
  );
}

export type TeamEnclosureProps = Omit<React.ComponentProps<"div">, "children"> & {
  /** The box around the team's cards, in the stage's coordinates (px). */
  rect: { left: number; top: number; width: number; height: number };
  label: string;
  /** TEAM-KPI: a small chip after the name (the team's score), on the same pill. */
  chip?: React.ReactNode;
};

/**
 * CHART-KB: a team's enclosure on the chart — a dashed contour with a tint around its seats'
 * cards and the team's name as a chip on its top edge (KPI Beast's team contour, as a box).
 * Decorative: the cards themselves carry the team.
 */
export function TeamEnclosure({
  rect,
  label,
  chip,
  className,
  style,
  ...props
}: TeamEnclosureProps) {
  return (
    <div
      data-slot="team-enclosure"
      aria-hidden="true"
      className={className}
      style={{ ...style, ...rect }}
      {...props}
    >
      <span data-slot="team-enclosure-label">
        {label}
        {chip}
      </span>
    </div>
  );
}

export type CompletenessRingProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** 0–100. */
  score: number;
  band: LensBandTone;
  /** `sm` is the card's 16 px ring with the number beside it; `lg` a 40 px ring with it inside. */
  size?: "sm" | "lg";
  label?: string;
};

const RING_STROKE: Record<LensBandTone, string> = {
  green: "stroke-band-great",
  amber: "stroke-band-average",
  red: "stroke-band-low",
  none: "stroke-band-insufficient-data",
};

/**
 * CHART-KB: the seat's setup completeness as a ring — the share in the band's colour over a
 * faint track — with the percentage beside (sm) or inside (lg) it; never colour alone.
 */
export function CompletenessRing({
  score,
  band,
  size = "sm",
  label,
  className,
  ...props
}: CompletenessRingProps) {
  const r = size === "sm" ? 6 : 16;
  const box = size === "sm" ? 16 : 40;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  return (
    <span
      data-slot="completeness-ring"
      data-band={band}
      role="img"
      aria-label={label ?? `Seat setup ${pct}% complete`}
      title={label ?? `Seat setup ${pct}% complete`}
      className={cn(
        "inline-flex shrink-0 items-center tabular-nums",
        size === "sm" ? "gap-1 text-2xs text-muted-foreground" : "relative text-sm font-semibold",
        className,
      )}
      {...props}
    >
      <svg
        aria-hidden="true"
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        className="shrink-0 -rotate-90"
      >
        <circle
          cx={box / 2}
          cy={box / 2}
          r={r}
          fill="none"
          strokeWidth={size === "sm" ? 2.5 : 4}
          className="stroke-border"
        />
        <circle
          cx={box / 2}
          cy={box / 2}
          r={r}
          fill="none"
          strokeWidth={size === "sm" ? 2.5 : 4}
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          className={RING_STROKE[band]}
        />
      </svg>
      <span className={size === "lg" ? "absolute inset-0 flex items-center justify-center" : ""}>
        {pct}%
      </span>
    </span>
  );
}

export type LensStepsProps = Omit<React.ComponentProps<"span">, "children"> & {
  /** Each step in display order: its band and its text ("≥ 80", "> 100 %"). */
  steps: { band: LensBandTone; text: string }[];
};

/** The inline legend of a lens's steps: "● ≥ 80  ● ≥ 60  ● below" in 12 px. */
export function LensSteps({ steps, className, ...props }: LensStepsProps) {
  return (
    <span
      data-slot="lens-steps"
      className={cn(
        "inline-flex min-w-0 items-center gap-3 text-xs text-muted-foreground",
        className,
      )}
      {...props}
    >
      {steps.map((s) => (
        <span
          key={`${s.band}-${s.text}`}
          className="inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          <LensBandDot band={s.band} />
          {s.text}
        </span>
      ))}
    </span>
  );
}
